import { getWecomConfig, getWecomJssdkConfig, generateAuthUrl } from '@/services/api';
import type { WecomConfig, WecomLoginParams } from '@/services/typings';

/**
 * 企业微信登录工具类
 */
export class WecomLoginUtil {
  private static instance: WecomLoginUtil;
  private config: WecomConfig;

  private constructor() {
    // 初始化时设置默认配置
    this.config = this.getDefaultConfig();
  }

  public static getInstance(): WecomLoginUtil {
    if (!WecomLoginUtil.instance) {
      WecomLoginUtil.instance = new WecomLoginUtil();
    }
    return WecomLoginUtil.instance;
  }

  /**
   * 获取默认配置
   */
  private getDefaultConfig(): WecomConfig {
    return {
      corpId: '',
      agentId: '',
      redirectUri: '',
      state: 'wecom_login',
      scope: 'snsapi_base',
    };
  }

  /**
   * 初始化企业微信配置
   */
  public async initConfig(): Promise<WecomConfig> {
    try {
      const response = await getWecomConfig();
      if (response.ret) {
        this.config = response.data;
        return this.config;
      } else {
        throw new Error(response.msg || '获取企业微信配置失败');
      }
    } catch (error) {
      console.error('初始化企业微信配置失败:', error);
      // 保持默认配置，避免阻塞
      return this.config;
    }
  }

  /**
   * 生成企业微信授权URL
   */
  public async generateAuthUrl(state?: string): Promise<string> {
    try {
      const response = await generateAuthUrl();
      if (response.ret) {
        //`https://open.weixin.qq.com/connect/oauth2/authorize?appid=${corpId}&agentid=${agentId}&redirect_uri=https%3A%2F%2Fscrm.liyeai.com%2Flogin&response_type=code&scope=${scope}&state=#wechat_redirect`    
        return response.data;
      } else {
        throw new Error(response.msg || '生成企业微信授权URL失败');
      }
    } catch (error) {
      console.error('生成企业微信授权URL失败:', error);
      // 保持默认配置，避免阻塞
      return window.location.origin + '/login?error=config_failed';
    }
  }

  /**
   * 从URL中获取授权码
   */
  public getAuthCodeFromUrl(): string | null {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('code');
    } catch (error) {
      console.error('获取授权码失败:', error);
      return null;
    }
  }

  /**
   * 从URL中获取state参数
   */
  public getStateFromUrl(): string | null {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('state');
    } catch (error) {
      console.error('获取state参数失败:', error);
      return null;
    }
  }

  /**
   * 检查是否为企业微信授权回调
   */
  public isWecomCallback(): boolean {
    try {
      return !!this.getAuthCodeFromUrl();
    } catch (error) {
      console.error('检查企业微信回调失败:', error);
      return false;
    }
  }

  /**
   * 获取登录参数
   */
  public getLoginParams(): WecomLoginParams | null {
    try {
      const code = this.getAuthCodeFromUrl();
      const state = this.getStateFromUrl();
      
      if (!code) {
        return null;
      }

      return { code, state: state || undefined };
    } catch (error) {
      console.error('获取登录参数失败:', error);
      return null;
    }
  }

  /**
   * 清除URL中的授权参数
   */
  public clearAuthParams(): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      
      // 使用 replaceState 避免在浏览器历史中留下记录
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      console.error('清除授权参数失败:', error);
      // 如果失败，尝试使用简单的重定向
      try {
        window.location.href = window.location.pathname;
      } catch (redirectError) {
        console.error('重定向失败:', redirectError);
      }
    }
  }

  /**
   * 检查是否在企业微信环境中
   */
  public isInWecom(): boolean {
    try {
      return /wxwork/.test(navigator.userAgent.toLowerCase());
    } catch (error) {
      console.error('检查企业微信环境失败:', error);
      return false;
    }
  }

  /**
   * 获取企业微信JSSDK配置
   */
  public async getJssdkConfig(): Promise<any> {
    try {
      if (!this.config) {
        await this.initConfig();
      }

      // 这里需要调用后端接口获取JSSDK配置
      // 实际实现需要根据后端接口调整
      try {
        const result = await getWecomJssdkConfig(JSON.stringify({
          url: window.location.href.split('#')[0], // 当前页面URL，不包含hash
        }));

        if (result.code === 0) {
          return result.data;
        } else {
          throw new Error(result.msg || '获取JSSDK配置失败');
        }
      } catch (error) {
        console.error('获取JSSDK配置失败:', error);
        // 返回默认配置，避免阻塞
        return {
          corpId: 'default_corp_id',
          timestamp: Date.now(),
          nonceStr: 'default_nonce',
          signature: 'default_signature',
        };
      }
    } catch (error) {
      console.error('获取JSSDK配置失败:', error);
      // 返回默认配置
      return {
        corpId: 'default_corp_id',
        timestamp: Date.now(),
        nonceStr: 'default_nonce',
        signature: 'default_signature',
      };
    }
  }
}

// 导出单例实例
export const wecomLoginUtil = WecomLoginUtil.getInstance();
